<template>
    <CardTabNavigation v-bind="cardTabNavigation" :headline-id="`v${variant.index}`" @focus-tab-panel="focusTabPanel">
        <template #header="{ headlineId }">
            <div :id="headlineId" class="flex flex-row items-center gap-2.5 pl-2 h-full">
                <div class="text-2xl">
                    <AllocationVariantIndex :index="variant.index" text-size="Inherit" />
                </div>
                <span class="text-gray-400 font-light">&ndash;</span>
                <InlineTextEdit v-bind="inlineTextEdit" @save="(name, onDone) => onSaveInlineTextedit(name, onDone)">
                    <template #editable-content>{{ values.name }}</template>
                </InlineTextEdit>

                <BadgeDropDown
                    :label="$t('event-variant-status-label')"
                    class="text-base font-normal"
                    :current-value="getVariantStatusItem(variant.status)"
                    :values="getVariantStatusOptionItems(variant.status)"
                    :status="dropDownStatus"
                    :tooltip="dropDownDisabledExplanation"
                    size="Medium-32"
                    @change-value="handleBadgeDropdown($event, variant.id)"
                />

                <MainVariantBadgeButton
                    :status="mainVariantBadgeButtonStatus"
                    :disabled-explanation="mainVariantBadgeButtonDisabledExplanation"
                    :color="categoryColor"
                    @click="onSetCurrentVariant(variant.id)"
                />

                <CopyLinkButton :link-target="variantLinkTargetString" :link-title="variant.linkTitle" />
            </div>
        </template>

        <template #body="{ activeSyncId }">
            <CardTabPanel v-bind="tilesPanel" ref="tilesPanelRef" :active-sync-id="activeSyncId">
                <div class="flex flex-row justify-end gap-1 p-1">
                    <div class="flex flex-row gap-1">
                        <ExternalEventVariantCardTiles
                            v-if="phaseTypes.length > 0 && variant"
                            :variant-id="variant.id"
                            :variant-index="variant.index"
                            :phase-types="phaseTypes"
                            :reference-allocation-id="referenceAllocationId"
                            :reference-allocation-query-document="referenceAllocationQueryDocument"
                            :reference-allocation-link-label="referenceAllocationLinkLabel"
                            :is-national="isNational"
                            :link-to-reference-allocation="linkToReferenceAllocation"
                        />
                    </div>
                </div>
            </CardTabPanel>

            <CardTabPanel v-bind="feedPanel" ref="feedPanelRef" :active-sync-id="activeSyncId">
                <div class="p-4 max-w-4xl">
                    <Feed>
                        <AddDateCommentEntry
                            @input="
                                (event: Event) =>
                                    handleAddDateCommentEntryInput((event.target as HTMLInputElement).value)
                            "
                            @create-comment="onCreateComment"
                        />
                        <template v-for="entry in feedEntries" :key="entry.id">
                            <DateCommentEntry
                                v-if="entry.type === 'user-text'"
                                :text="entry.text"
                                image-url=""
                                :first-name="entry.createdBy!.firstName"
                                :last-name="entry.createdBy!.lastName"
                                :user-page-link="{ name: 'user-details', params: { userId: entry.createdBy!.id } }"
                                :date-time="DateTime.fromISO(entry.createdAt)"
                                :is-editable="entry.isEditable && isUserReady"
                                :has-unsaved-changes="entry.hasUnsavedChanges"
                                @delete-comment="onDirectDelete(entry.id)"
                                @update-comment="(text) => onDirectUpdate(entry.id, text)"
                                @undo-deleting-comment="undoDelete(entry.id, entry.hasUnsavedChanges)"
                            />
                            <NewMainVariantEntry
                                v-else-if="
                                    entry.type === 'new-main-variant' &&
                                    entry.oldMainVariantIndex &&
                                    entry.newMainVariantIndex
                                "
                                :user-name="entry.createdByName"
                                :user-page-link="{ name: 'user-details', params: { userId: entry.createdBy!.id } }"
                                :previous-main-variant-name="
                                    getUnstyledAllocationVariantIndex(entry.oldMainVariantIndex)
                                "
                                :previous-main-variant-link-target="
                                    getVariantRoute(variantType, externalEventVariantId, entry.oldMainVariantIndex)
                                "
                                :new-main-variant-name="getUnstyledAllocationVariantIndex(entry.newMainVariantIndex)"
                                :new-main-variant-link-target="
                                    getVariantRoute(variantType, externalEventVariantId, entry.newMainVariantIndex)
                                "
                                :date-time="DateTime.fromISO(entry.createdAt)"
                            />
                            <StatusChangeEntry
                                v-else-if="entry.type === 'new-status'"
                                :new-status="getVariantStatusLabel(variantType, entry.newStatus)"
                                :previous-status="getVariantStatusLabel(variantType, entry.oldStatus)"
                                :date-time="DateTime.fromISO(entry.createdAt)"
                                :user-name="entry.createdByName"
                                :user-page-link="{ name: 'user-details', params: { userId: entry.createdBy!.id } }"
                            />
                            <CreationDateEntry
                                v-else-if="entry.type === 'created-at' && entry.objectCreatedAt"
                                :user-name="entry.createdByName"
                                :user-page-link="{ name: 'user-details', params: { userId: entry.createdBy!.id } }"
                                :date-time="DateTime.fromISO(entry.objectCreatedAt)"
                            />
                        </template>
                    </Feed>
                </div>
            </CardTabPanel>
        </template>
    </CardTabNavigation>
</template>
<script setup lang="ts">
import auth from '@/auth/auth';
import { AllocationGroupType } from '@/components/AllocationSelectionPanel/AllocationSelectionPanel.types.ts';
import AllocationVariantIndex from '@/components/AllocationVariantIndex/AllocationVariantIndex.vue';
import { getUnstyledAllocationVariantIndex } from '@/components/AllocationVariantIndex/getFullAllocationVariantIndex';
import BadgeDropDown from '@/components/BadgeDropDown/BadgeDropDown.vue';
import CardTabNavigation from '@/components/CardTabNavigation/CardTabNavigation.vue';
import CardTabPanel from '@/components/CardTabNavigation/CardTabPanel.vue';
import CopyLinkButton from '@/components/CopyLinkButton/CopyLinkButton.vue';
import AddDateCommentEntry from '@/components/Feed/AddDateCommentEntry.vue';
import CreationDateEntry from '@/components/Feed/CreationDateEntry.vue';
import DateCommentEntry from '@/components/Feed/DateCommentEntry.vue';
import Feed from '@/components/Feed/Feed.vue';
import NewMainVariantEntry from '@/components/Feed/NewMainVariantEntry.vue';
import StatusChangeEntry from '@/components/Feed/StatusChangeEntry.vue';
import InlineTextEdit from '@/components/InlineTextEdit/InlineTextEdit.vue';
import MainVariantBadgeButton from '@/components/MainVariantBadgeButton/MainVariantBadgeButton.vue';
import { AllocationUsage } from '@/components/Timeline/allocation/types';
import { PhaseColor } from '@/components/Timeline/Timeline.types.ts';
import { UpdateEventVariantNameSchema } from '@/event/forms/schemas';
import { useFeed } from '@/feed/composables/useFeed';
import { useForm } from '@/forms/composables/useForm';
import { $t } from '@/plugins/fluent';
import { getAbsoluteURI } from '@/utils/url';
import { useVariantStatusDropdown } from '@/variant/composables/useVariantStatusDropdown';
import { VariantCardVariant } from '@/variant/types';
import { getVariantRoute } from '@/variant/utils';
import { DocumentNode } from 'graphql';
import { DateTime } from 'luxon';
import { computed, nextTick, Ref, ref, useTemplateRef } from 'vue';
import { RouteLocationRaw, useRouter } from 'vue-router';
import { useExternalEventVariantUpdate } from '../composables/useExternalEventVariantUpdate';
import ExternalEventVariantCardTiles from './ExternalEventVariantCardTiles.vue';

type VariantCardProps = {
    externalEventVariantId: string;
    categoryColor: Extract<
        PhaseColor,
        | 'External-Event-Category-Project'
        | 'External-Event-Category-Event'
        | 'External-Event-Category-External-Event'
        | 'Project'
        | 'Event'
    >;
    variant: VariantCardVariant;
    variantsRef: VariantCardVariant[];
    phaseTypes: AllocationUsage[];
    referenceAllocationQueryDocument: DocumentNode;
    referenceAllocationLinkLabel: string;
    setCurrentVariantLoading: boolean;
    setCurrentVariantError: boolean;
    isNational: boolean;
    setVariantsRef: (value: VariantCardVariant[]) => void;
    linkToReferenceAllocation: RouteLocationRaw;
};

const props = defineProps<VariantCardProps>();

type EventCardEmits = {
    'set-current-variant': [variantId: string];
};

const emit = defineEmits<EventCardEmits>();

const variant = computed(() => props.variant);
const variantType: Ref<AllocationGroupType> = ref('ExternalEvent');
const feedId = computed(() => variant.value.changesId);
const variantsRef = computed(() => props.variantsRef);
const setCurrentVariantLoading = computed(() => props.setCurrentVariantLoading);
const setCurrentVariantError = computed(() => props.setCurrentVariantError);
const referenceAllocationId = computed(() => {
    return variant.value.referenceAllocationId ?? undefined;
});

const mainVariantBadgeButtonDisabledExplanation = computed(() => {
    if (!auth.isAdmin()) return $t('no-permission-to-edit');
});

const newEntry = ref('');

function handleAddDateCommentEntryInput(value: string) {
    newEntry.value = value;
}

const { updateExternalEventVariantName } = useExternalEventVariantUpdate();

const { values, hasErrors, setFieldValue } = useForm(
    UpdateEventVariantNameSchema,
    computed(() => ({
        name: variant.value.name,
    })),
);

async function onSaveInlineTextedit(name: string, onDone: (error?: Error) => void) {
    await setFieldValue('name', name);
    if (hasErrors.value) {
        onDone(new Error('Invalid value in name field'));
        return;
    }
    const result = await updateExternalEventVariantName(variant.value.id, name, variant.value.changesId);
    if (result?.errors) {
        onDone(new Error('Error on saving variant name'));
        return;
    }
    onDone();
}

// Feed logic and expose create main variant entry
const {
    feedEntries,
    createStatusChangeEntry,
    createUserTextEntry,
    onDirectDelete,
    onDirectUpdate,
    undoDelete,
    createNewMainVariantEntry,
    isUserReady,
} = useFeed(feedId);

function onCreateComment(text: string, onDone: (error?: Error) => void) {
    createUserTextEntry(text);
    // TODO Implement correctly
    onDone();
}

export type ExternalEventVariantCardExpose = {
    id: string;
    createNewMainVariantEntry: (oldId: string, oldIndex: number, newId: string, newIndex: number) => Promise<void>;
};

defineExpose({
    id: props.variant.id,
    createNewMainVariantEntry: createNewMainVariantEntry,
});

const router = useRouter();
const variantLinkTargetString = computed(() => {
    const link = router.resolve(variant.value.linkTarget);
    return getAbsoluteURI(link.fullPath);
});

const mainVariantBadgeButtonStatus = computed(() => {
    if (variant.value.isCurrent && !setCurrentVariantError.value && !setCurrentVariantLoading.value) {
        return 'Main-Variant' as const;
    }
    if (!variant.value.isCurrent && !setCurrentVariantError.value && !setCurrentVariantLoading.value) {
        return 'Not-Main-Variant' as const;
    }
    if (setCurrentVariantLoading.value && !setCurrentVariantError.value) {
        return 'Pending' as const;
    }
    if (!setCurrentVariantLoading.value && setCurrentVariantError.value) {
        return 'Error' as const;
    }
    // FIXME ugly returning this as default
    return 'Not-Main-Variant' as const;
});

function onSetCurrentVariant(variantId: string) {
    // do not emit because that would mean to unset the current variant
    // without setting a new variant as current
    if (variant.value.isCurrent) {
        return;
    }
    emit('set-current-variant', variantId);
}

const inlineTextEdit = computed(() => ({
    label: $t('variant-name'),
    value: values.value.name ?? '',
    disabledExplanation: auth.isAdmin() ? undefined : $t('no-permission-to-edit'),
}));

const cardTabNavigation = computed(() => {
    return {
        items: [
            {
                syncId: `${variant.value.id}-tiles`,
                tabPanelId: `${variant.value.id}-tiles-panel`,
                label: $t('tiles'),
                icon: 'Tiles' as const,
            },
            {
                syncId: `${variant.value.id}-feed`,
                tabPanelId: `${variant.value.id}-feed-panel`,
                label: `${$t('entries')} (${feedEntries.value.length})`,
                icon: 'Feed' as const,
            },
        ],
    };
});

// Tab Panels

const tilesPanelRef = useTemplateRef('tilesPanelRef');
const feedPanelRef = useTemplateRef('feedPanelRef');

const tilesPanel = {
    syncId: `${variant.value.id}-tiles`,
    tabPanelId: `${variant.value.id}-tiles-panel`,
};
const feedPanel = {
    syncId: `${variant.value.id}-feed`,
    tabPanelId: `${variant.value.id}-feed-panel`,
};

const focusTabPanel = async () => {
    await nextTick();
    tilesPanelRef.value?.focus();
    feedPanelRef.value?.focus();
};

const {
    dropDownStatus,
    dropDownDisabledExplanation,
    getVariantStatusItem,
    getVariantStatusLabel,
    getVariantStatusOptionItems,
    handleBadgeDropdown,
} = useVariantStatusDropdown(variantType, variant, variantsRef, props.setVariantsRef, createStatusChangeEntry);
</script>
